import { Injectable, signal } from "@angular/core";
import { IMessage, IMessageAttachment } from '../domain/message';
import { ArrayUtils } from "../utils/array.utils";
import { StringUtils } from "../utils/string.utils";

export type UserMessageInput = { userText: string, attachments?: IMessageAttachment[] };

@Injectable({ providedIn: 'root' })
export class ChatStore {
    private _currentResponseMessage: IMessage | null = null;
    private _currentRequestMessage: IMessage | null = null;
    private _pendingConversationId: string | null = null;

    private readonly _messages = signal<IMessage[]>([]);

    public connectionId = signal('');
    public conversationId = signal<string | null>(null);
    public readonly messages = this._messages.asReadonly();

    public setPendingConversationId(conversationId: string) {
        this._pendingConversationId = conversationId;
    }

    public revokePendingConversationId(conversationId: string) {
        if (this._pendingConversationId === conversationId) {
            this.conversationId.set(conversationId);
            this._pendingConversationId = null;
            return true;
        }

        return false;
    }

    public setMessagesFromHistory(
        conversationId: string,
        messages: {
            id: string;
            userText: string;
            isResponse: boolean;
            attachments?: IMessageAttachment[] | null;
            incomplete: boolean
        }[],
    ) {
        this._currentResponseMessage = null;
        this._currentRequestMessage = null;

        this.conversationId.set(conversationId);

        const messagesToSet = messages.map((m) => ({
                id: m.id,
                text: signal(m.userText),
                audioBytes: signal(new Uint8Array()),
                isResponse: m.isResponse,
                conversationId: conversationId,
                attachments: m.attachments && m.attachments.length > 0 ? [...m.attachments] : null,
                incomplete: signal(m.incomplete)
            }));

        this._messages.set(messagesToSet);

        if (messagesToSet.length > 0) {
            const lastMessage = messagesToSet[messagesToSet.length - 1];
            if (lastMessage.isResponse) {
                this._currentResponseMessage = lastMessage;
            }
        }
    }

    public addUserMessage(payload: UserMessageInput) {
        const newMessage = {
            id: crypto.randomUUID(),
            text: signal(payload.userText),
            audioBytes: signal(new Uint8Array()),
            isResponse: false,
            conversationId: this.conversationId(),
            attachments: payload.attachments ?? null,
            incomplete: signal(false)
        };

        this._currentResponseMessage = null;
        this._currentRequestMessage = newMessage;
        this._messages.update((messages) => [...messages, newMessage]);
    }

    public getLastMessage(): IMessage | null {
        if (this.messages().length === 0) {
            return null;
        }
        return this.messages()[this.messages().length - 1];
    }

    public appendLastMessageText(newText: string) {
        this.acquireCurrentResponseMessage().text.update(
            (currentText) => (currentText ?? '') + newText,
        );
    }

    public appendLastMessageAudioBytes(audioBase64: string) {
        const responseMessage = this.acquireCurrentResponseMessage();
        responseMessage.audioBytes.update((currentBytes) =>
            this.getAudioBytes(currentBytes, audioBase64),
        );

        return responseMessage;
    }

    public startNewConversation() {
        this.conversationId.set(null);
        this._messages.set([]);
    }

    private getAudioBytes(audioBytes: any, audioBase64: string) {
        const newBytes = StringUtils.base64ToBytes(audioBase64);
        return ArrayUtils.mergeUInt8Arrays(audioBytes, newBytes);
    }

    private acquireCurrentResponseMessage(): IMessage {
        if (this._currentResponseMessage !== null) {
            return this._currentResponseMessage;
        }

        const newMessage: IMessage = {
            id: crypto.randomUUID(),
            text: signal(''),
            audioBytes: signal(new Uint8Array()),
            isResponse: true,
            conversationId: this.conversationId(),
            attachments: null,
            incomplete: signal(false)
        };

        this._messages.update((current) => [...current, newMessage]);
        this._currentResponseMessage = newMessage;

        return newMessage;
    }

    public appendLastRequestText(text: string) {
        if (this._currentRequestMessage !== null) {
            this._currentRequestMessage.text.update(current => (current ?? '') + text);
        }
    }

    public setLastResponseMessagePayload(messageId: string, text: string, audioBase64: string) {
        // We call this in very end of response generation to recover
        // response text in case when it was interrupted by page reload.

        if (this._currentResponseMessage !== null && this._currentResponseMessage.id === messageId) {
            this._currentResponseMessage.text.set(text);
            this._currentResponseMessage.audioBytes.set(
                StringUtils.base64ToBytes(audioBase64)
            );
            this._currentResponseMessage.incomplete.set(false);
        }
    }
}
